"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import CdnImage from "@/components/CdnImage";

interface RelatedSeriesItem {
  seriesTicker: string;
  eventTicker: string;
  title: string;
  subtitle: string | null;
  category: string | null;
  image: string | null;
  icon: string | null;
  volume: number;
  activeMarkets: number;
  topMarkets: Array<{ ticker: string; title: string; yesPrice: number | null }>;
}

export default function KalshiRelatedSeries({ seriesTicker }: { seriesTicker: string }) {
  const [items, setItems] = useState<RelatedSeriesItem[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/markets/kalshi/related?ticker=${encodeURIComponent(seriesTicker)}`)
      .then(r => r.ok ? r.json() : { related: [] })
      .then(d => setItems(d.related || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [seriesTicker]);

  if (loading || items.length === 0) return null;

  const INITIAL_LIMIT = 5;
  const displayed = showAll ? items : items.slice(0, INITIAL_LIMIT);
  const formatVol = (v: number) => {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
    return `$${v}`;
  };

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-[#1A1A1A]">
        <div className="flex items-center gap-1.5">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 dark:text-gray-500"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          <h3 className="font-monument text-base md:text-lg text-gray-900 dark:text-white">People are also trading</h3>
        </div>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-[#1A1A1A]/50">
        {displayed.map((item) => (
          <Link
            key={item.eventTicker}
            href={`/kalshi/event/${item.eventTicker}/${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
            className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
          >
            {item.image ? (
              <CdnImage src={item.image} alt="" width={40} height={40} className="w-10 h-10 rounded-[5px] object-cover flex-shrink-0 mt-0.5" />
            ) : (
              <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="font-monument text-xs text-gray-400">{item.title?.charAt(0) || '?'}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-inter-tight text-sm font-medium text-gray-900 dark:text-white line-clamp-2 leading-tight">
                {item.title}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] font-inter-tight text-gray-400">{formatVol(item.volume)} Vol</span>
                {item.activeMarkets > 1 && (
                  <span className="text-[10px] font-inter-tight text-gray-400">{item.activeMarkets} markets</span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
      {items.length > INITIAL_LIMIT && (
        <div className="px-3 py-2 border-t border-gray-200 dark:border-[#1A1A1A]">
          <button
            onClick={() => setShowAll(!showAll)}
            className="w-full py-2 text-center text-xs font-inter-tight font-semibold text-gray-600 dark:text-gray-400 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] hover:bg-gray-50 dark:hover:bg-[#0f0f12] transition-colors"
          >
            {showAll ? 'Show less' : `Show all (${items.length})`}
          </button>
        </div>
      )}
    </div>
  );
}
